import React, { useState, useRef, useEffect } from 'react';
import { Upload, X } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';

interface BusinessLogoUploadProps {
  className?: string;
  size?: 'small' | 'medium' | 'large';
}

export default function BusinessLogoUpload({ className = '', size = 'medium' }: BusinessLogoUploadProps) {
  const { selectedBusiness, updateBusiness } = useBusiness();
  const [isUploading, setIsUploading] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Size configurations
  const sizeConfig = {
    small: {
      container: 'h-12',
      button: 'px-2 py-1 text-xs',
      image: 'h-12',
      icon: 'w-3 h-3',
      deleteButton: 'w-4 h-4 text-xs -top-1 -right-1'
    },
    medium: {
      container: 'h-16',
      button: 'px-3 py-2 text-xs',
      image: 'h-16',
      icon: 'w-3 h-3',
      deleteButton: 'w-5 h-5 text-xs -top-2 -right-2'
    },
    large: {
      container: 'h-24',
      button: 'px-4 py-3 text-sm',
      image: 'h-24',
      icon: 'w-4 h-4',
      deleteButton: 'w-6 h-6 text-sm -top-3 -right-3'
    }
  };

  const config = sizeConfig[size];

  // Load logo URL from business settings
  useEffect(() => {
    if (selectedBusiness?.booking_settings?.logo_url) {
      setLogoUrl(selectedBusiness.booking_settings.logo_url);
    } else {
      setLogoUrl(null);
    }
  }, [selectedBusiness]);

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedBusiness) return;

    setIsUploading(true);

    try {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('Please select an image file');
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error('File size must be less than 5MB');
      }

      console.log('Uploading logo file:', file.name, 'Size:', file.size);

      // Upload to Supabase storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${selectedBusiness.id}/logo-${Date.now()}.${fileExt}`;
      const bucketName = 'avatars';

      const { data, error } = await supabase.storage
        .from(bucketName)
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;

      // Get public URL
      const { data: urlData } = await supabase.storage
        .from(bucketName)
        .getPublicUrl(fileName);

      if (!urlData?.publicUrl) {
        throw new Error('Failed to get public URL for uploaded file');
      }

      // Update business settings with logo URL
      console.log('Updating business settings with logo URL:', urlData.publicUrl);
      const updatedSettings = {
        ...selectedBusiness.booking_settings,
        logo_url: urlData.publicUrl
      };

      await updateBusiness(selectedBusiness.id, {
        booking_settings: updatedSettings
      });

      setLogoUrl(urlData.publicUrl);
      toast.success('Logo uploaded successfully');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteLogo = async () => {
    if (!selectedBusiness || !logoUrl) return;

    try {
      // Extract the file path from the URL
      const urlParts = logoUrl.split('/');
      const filePath = urlParts[urlParts.length - 2] + '/' + urlParts[urlParts.length - 1];

      // Delete file from storage
      const bucketName = 'avatars';
      const { error: deleteError } = await supabase.storage
        .from(bucketName)
        .remove([filePath]);

      if (deleteError) {
        console.warn('Error deleting logo from storage:', deleteError);
        // Continue with business update even if storage deletion fails
      }

      // Update business settings to remove logo URL
      const updatedSettings = {
        ...selectedBusiness.booking_settings,
        logo_url: undefined
      };

      await updateBusiness(selectedBusiness.id, {
        booking_settings: updatedSettings
      });

      setLogoUrl(null);
      toast.success('Logo removed successfully');
    } catch (error) {
      console.error('Error deleting logo:', error);
      toast.error('Failed to remove logo: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  if (!selectedBusiness) {
    return null;
  }

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Business Logo
      </label>
      <div className="space-y-2">
        {logoUrl && (
          <div className="relative inline-block">
            <img
              src={logoUrl}
              alt="Business logo"
              className={`${config.image} max-w-full object-contain border border-gray-300 rounded bg-gray-50 p-2`}
            />
            <button
              onClick={handleDeleteLogo}
              className={`absolute ${config.deleteButton} bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors`}
              title="Remove logo"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}
        
        <input
          type="file"
          accept="image/*"
          onChange={handleLogoUpload}
          className="hidden"
          id={`logo-upload-${size}`}
          ref={fileInputRef}
          disabled={isUploading}
        />
        
        <label
          htmlFor={`logo-upload-${size}`}
          className={`
            flex items-center justify-center w-full ${config.button}
            text-gray-600 bg-white border border-gray-300 rounded cursor-pointer 
            hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 
            focus:border-blue-500 shadow-sm transition-colors
            ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <Upload className={`${config.icon} mr-2`} />
          {isUploading ? 'Uploading...' : logoUrl ? 'Change Logo' : 'Upload Logo'}
        </label>
      </div>
      
      <p className="text-xs text-gray-500 mt-1">
        Upload an image file for your business logo (recommended: 200x60px, max 5MB). Logo will appear in your online booking header.
      </p>
    </div>
  );
}
